import React, { useState, useEffect } from 'react';
import { getCurrentSessionToken, updateUserProfile } from '../../auth';
import type { UserProfile } from '../../auth';

interface ToastItem {
  id: string;
  type: 'success' | 'danger';
  message: string;
  description?: string;
}

interface ProfilePageProps {
  userProfile: UserProfile | null;
  onProfileUpdated: () => void;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ userProfile, onProfileUpdated }) => {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = (message: string, type: ToastItem['type'] = 'success', description?: string) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, { id, type, message, description }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  useEffect(() => {
    if (userProfile) {
      setFullName(userProfile.full_name || '');
      setPhone(userProfile.phone || '');
    }
  }, [userProfile, isEditing]);

  const handleSave = async () => {
    if (!fullName.trim()) {
      showToast('Campo requerido', 'danger', 'El nombre completo es obligatorio.');
      return;
    }
    if (!phone.trim()) {
      showToast('Campo requerido', 'danger', 'El teléfono es obligatorio.');
      return;
    }

    try {
      setIsSaving(true);
      const token = await getCurrentSessionToken();
      if (!token) {
        throw new Error('No active session token found');
      }

      await updateUserProfile({
        full_name: fullName.trim(),
        phone: phone.trim()
      }, token);

      showToast('Perfil actualizado', 'success', 'Tus datos se guardaron correctamente.');
      setIsEditing(false);
      onProfileUpdated();
    } catch (e: any) {
      console.error('Error updating profile:', e);
      showToast('Error al actualizar', 'danger', e?.message || 'Intentá de nuevo más tarde.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Mi perfil</h1>
        <p className="text-muted-foreground mt-1">
          Gestioná tus datos de contacto
        </p>
      </div>

      <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm" style={{ borderTop: '3px solid #006837' }}>
        <div className="p-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(91,186,71,0.15)', color: '#006837' }}>
              <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight">Información personal</h2>
              <p className="text-sm text-muted-foreground">Gestioná tus datos de contacto y rol</p>
            </div>
          </div>
          
          {!isEditing && (
            <button 
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2 border border-border rounded-lg hover:bg-muted transition text-sm font-semibold shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
              Editar
            </button>
          )}
        </div>

        <div className="px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <div>
              <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Nombre y Apellido</label>
              <input 
                type="text" 
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                readOnly={!isEditing || isSaving} 
                className={`w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm ${!isEditing ? 'bg-[#F5F7F4] border-transparent text-[#212121] cursor-default' : 'bg-white'}`}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">CUIT</label>
              <input 
                type="text" 
                value={userProfile?.CUIT || ''} 
                readOnly
                className="w-full px-3 py-2 border border-transparent rounded-lg focus:outline-none text-sm bg-[#F5F7F4] text-[#212121] cursor-default"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Teléfono</label>
              <input 
                type="tel" 
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                readOnly={!isEditing || isSaving} 
                className={`w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm ${!isEditing ? 'bg-[#F5F7F4] border-transparent text-[#212121] cursor-default' : 'bg-white'}`}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Correo electrónico</label>
              <input 
                type="email" 
                value={userProfile?.email || ''} 
                readOnly
                className="w-full px-3 py-2 border border-transparent rounded-lg focus:outline-none text-sm bg-[#F5F7F4] text-[#212121] cursor-default"
              />
            </div>
          </div>

          {isEditing && (
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-border">
              <button 
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
                className="px-4 py-2 border border-input rounded-lg hover:bg-muted transition font-semibold text-sm disabled:opacity-50"
              >
                Cancelar
              </button>
              <button 
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-semibold shadow-sm transition text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {isSaving && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {isSaving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toast container */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 max-w-sm w-full pointer-events-none">
        {toasts.map(t => (
          <div 
            key={t.id} 
            className={`pointer-events-auto p-4 rounded-xl shadow-lg border backdrop-blur-md flex gap-3 toast-slide-in transition duration-300 ${
              t.type === 'success' 
                ? 'bg-white/95 border-emerald-100 text-emerald-800 shadow-emerald-500/10' 
                : 'bg-white/95 border-red-100 text-red-800 shadow-red-500/10'
            }`}
          >
            <div className="flex-shrink-0 mt-0.5">
              {t.type === 'success' ? (
                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              ) : (
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              )}
            </div>
            <div className="flex-grow">
              <h4 className="font-bold text-sm text-gray-900">{t.message}</h4>
              {t.description && (
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{t.description}</p>
              )}
            </div>
            <button 
              onClick={() => setToasts(prev => prev.filter(item => item.id !== t.id))}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
